// 产品矩阵系统 - 主入口
import React from 'react';
import { DndContext, DragEndEvent, DragOverlay, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { useCanvasStore } from './stores/canvas-store';
import { useProductStore } from './stores/product-store';

interface PosterAppProps {
  children: React.ReactNode;
}

/**
 * 产品矩阵应用包装器
 * 提供拖拽上下文
 */
export function PosterApp({ children }: PosterAppProps) {
  const { addProductToCell } = useCanvasStore();
  const { getProductById } = useProductStore();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 需要拖动8px才激活
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    // 检查是否拖到了画布单元格
    const overId = over.id.toString();
    if (overId.startsWith('cell-')) {
      const targetCellIndex = parseInt(overId.replace('cell-', ''), 10);
      const dragData = active.data.current;

      // 情况1：从产品库拖入新产品
      if (dragData?.type === 'product' && dragData.product) {
        addProductToCell(targetCellIndex, dragData.product, dragData.model);
      }
      
      // 情况2：已放置产品的移动（从一个格子拖到另一个格子）
      if (dragData?.type === 'placed-product') {
        const sourceIndex = dragData.sourceIndex as number;
        // 如果拖到自己，不处理
        if (sourceIndex === targetCellIndex) return;
        
        // 移动产品：先获取源产品信息，再移动
        const { canvasData, removeProductFromCell, addProductToCell: addToCell } = useCanvasStore.getState();
        const sourceCell = canvasData.cells.find(c => c.index === sourceIndex);
        
        if (sourceCell && sourceCell.product_id) {
          const product = getProductById(sourceCell.product_id);
          if (product) {
            // 先移除源格子的产品
            removeProductFromCell(sourceIndex);
            // 再添加到目标格子
            addToCell(targetCellIndex, product, sourceCell.model_id ? product.models?.find(m => m.id === sourceCell.model_id) : undefined);
          }
        }
      }
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      {children}
    </DndContext>
  );
}

// 导出所有页面和组件
export { PosterCanvasPage } from './pages/PosterCanvasPage';
export { ProductManagePage } from './pages/ProductManagePage';
export { ProductHierarchyPage } from './pages/ProductHierarchyPage';
export { ProductSelectionWizard } from './pages/ProductSelectionWizard';
export { ProductLibrary } from './components/ProductLibrary';
export { CanvasGrid } from './components/CanvasGrid';
export { ExportModal } from './components/ExportModal';
export { CardStylePanel } from './components/CardStylePanel';

// 导出层级组件
export * from './components/hierarchy';

// 导出 stores
export { useProductStore } from './stores/product-store';
export { useCanvasStore } from './stores/canvas-store';
export { useHierarchyStore } from './stores/hierarchy-store';

// 导出类型
export * from './types';
export * from './types/hierarchy';

export default PosterApp;
